import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function MembershipSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed | expired
  const [details, setDetails] = useState(null);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/membership/checkout/status/${sessionId}`);
        setDetails(data);
        if (data.payment_status === "paid") {
          setState("paid");
          refresh();
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        if (attempts >= 8) {
          setState("expired");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (attempts >= 8) {
          setState("failed");
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-20">
      <div className="bg-white border border-stone-200 rounded-md p-12 text-center">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto text-stone-400 animate-spin" size={48} />
            <h1 className="mt-6 font-serif text-3xl text-stone-900">Confirming your membership...</h1>
            <p className="mt-3 text-stone-600 font-sans text-sm">Hold tight — Stripe is finalizing your payment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-emerald-700" size={56} />
            <h1 className="mt-6 font-serif text-4xl text-stone-900">Welcome to the network.</h1>
            <p className="mt-3 text-stone-600 font-sans">Your <span className="text-emerald-800 font-semibold">{details?.package_id === "promo" ? "Annual Founders Promo" : details?.package_id === "annual" ? "Annual Standard" : "Monthly"}</span> membership is active. From now on, ordering is free.</p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Button data-testid="goto-marketplace" onClick={() => navigate("/marketplace")} className="bg-emerald-800 hover:bg-emerald-700 text-stone-50 px-7 py-6 rounded-md">Start Ordering</Button>
              <Button data-testid="goto-dashboard" variant="outline" className="border-stone-300 px-7 py-6 rounded-md" onClick={() => navigate("/dashboard")}>Go to Dashboard</Button>
            </div>
          </>
        )}
        {state === "expired" && (
          <>
            <XCircle className="mx-auto text-amber-700" size={56} />
            <h1 className="mt-6 font-serif text-3xl text-stone-900">Session expired</h1>
            <p className="mt-3 text-stone-600 font-sans text-sm">No charge was made. You can try again from the membership page.</p>
            <Link to="/pricing"><Button className="mt-6 bg-emerald-800 hover:bg-emerald-700 text-stone-50">Back to Membership</Button></Link>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="mx-auto text-red-600" size={56} />
            <h1 className="mt-6 font-serif text-3xl text-stone-900">Something went wrong</h1>
            <p className="mt-3 text-stone-600 font-sans text-sm">We could not confirm your payment. If you were charged, contact us and we'll fix it.</p>
            <Link to="/contact"><Button className="mt-6 bg-emerald-800 hover:bg-emerald-700 text-stone-50">Contact Us</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}
